import React, { useState } from 'react';
import { collection, addDoc, query, getDocs, serverTimestamp } from 'firebase/firestore';
import { db, getTimeData } from '../firebase';
import { containsBlockedWords, getBlockedWordMessage } from '../blockedWords';

function SubmitQuestion({ onClose, onSubmit }) {
  const [questionText, setQuestionText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const normalizeText = (text) => {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s]/g, '') // Remove punctuation
      .replace(/\s+/g, ' '); // Normalize spaces
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (questionText.trim().length < 10) {
      alert('Question must be at least 10 characters long.');
      return;
    }

    if (questionText.trim().length > 200) {
      alert('Question must be 200 characters or less.');
      return;
    }

    // CHECK FOR BLOCKED WORDS
    const blockedCheck = containsBlockedWords(questionText);
    if (blockedCheck.blocked) {
      alert(getBlockedWordMessage());
      return;
    }

    setSubmitting(true);

    try {
      const timeData = getTimeData();
      const normalizedQuestion = normalizeText(questionText);
      
      // Check for ANY existing question (active OR expired) with same normalized text
      const allQuestionsQuery = query(collection(db, 'questions'));
      const allQuestionsSnapshot = await getDocs(allQuestionsQuery);
      
      let existingQuestion = null;
      allQuestionsSnapshot.forEach((doc) => {
        const docData = doc.data();
        const existingNormalized = docData.normalizedText || normalizeText(docData.text);
        if (existingNormalized === normalizedQuestion) {
          existingQuestion = { id: doc.id, ...docData };
        }
      });

      // Get user ID from localStorage
      let userId = localStorage.getItem('atc-user-id');
      if (!userId) {
        userId = 'user-' + Math.random().toString(36).substr(2, 9) + Date.now();
        localStorage.setItem('atc-user-id', userId);
      }

      // Always create a new active question (even if it's been asked before)
      const timesAsked = existingQuestion ? (existingQuestion.timesAsked || 1) + 1 : 1;
      
      const questionRef = await addDoc(collection(db, 'questions'), {
        text: questionText.trim(),
        normalizedText: normalizedQuestion,  // Store for aggregation
        votesA: 0,
        votesB: 0,
        totalVotes: 0,
        status: 'active',
        createdAt: serverTimestamp(),
        submittedAt: {
          hour: timeData.hour,
          dayOfWeek: timeData.dayOfWeek,
          date: timeData.date
        },
        isSponsored: false,
        timesAsked: timesAsked
      });

      // Create a "skip" vote so submitter doesn't see their own question
      await addDoc(collection(db, 'votes'), {
        questionId: questionRef.id,
        voterId: userId,
        choice: 'SKIP',
        timestamp: new Date(),
        votedAt: {
          hour: timeData.hour,
          dayOfWeek: timeData.dayOfWeek,
          date: timeData.date
        }
      });

      if (existingQuestion) {
        alert(`Great minds think alike! This question has been asked ${timesAsked} times. Your vote of interest has been recorded! 🔥`);
      }

      setQuestionText('');
      setSubmitting(false);
      onSubmit();
      onClose();
    } catch (error) {
      console.error('Error submitting question:', error);
      alert('Error submitting question. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full p-6">
        <h2 className="text-2xl font-bold text-atc-purple mb-4">Ask the Class</h2>
        
        <form onSubmit={handleSubmit}>
          <textarea
            value={questionText}
            onChange={(e) => setQuestionText(e.target.value)}
            placeholder="What decision do you need help with? (10-200 characters)"
            className="w-full p-4 border-2 border-atc-purple rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-atc-yellow"
            rows="4"
            maxLength="200"
            disabled={submitting}
          />
          
          <div className="text-right text-sm text-gray-500 mt-2 mb-4">
            {questionText.length}/200 characters
          </div>
          
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-3 px-6 rounded-lg transition-colors"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 bg-atc-yellow hover:bg-yellow-400 text-atc-purple font-bold py-3 px-6 rounded-lg transition-colors disabled:opacity-50"
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SubmitQuestion;